/**
 * Change Language modal (#2226, image 21).
 *
 * Replaces the old Globe dropdown. Shows:
 * - Original: the source doc's language (read-only).
 * - Translations: every existing translation as a radio-style row.
 *   The active document row is marked with a checkmark.
 * - "Add a translation" button → swaps to `<AddTranslationPanel>`
 *   which fires `useTranslateDocument` on confirm.
 *
 * The parent controls what selecting a translation means:
 *   - transcript-about / caption-about pass a navigating callback so
 *     the user opens the picked doc's editor.
 *   - split-view mode passes a callback that just swaps which
 *     translation renders in the right panel.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import Modal from '@components/atoms/modals/modal.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import Button from '@components/atoms/ui-kit/button.tsx';
import { AddTranslationPanel } from './add-translation-panel.tsx';
import { nameForCode } from './language-utils.ts';
import { SelectableRow } from './selectable-row.tsx';
import type { TranslationQuote } from '@utils/api/documents/use-translation-quote.ts';

export interface ChangeLanguageDoc {
  id: string;
  /** 2-letter code as returned by the API. */
  language: string | null | undefined;
  filename?: string | null;
}

export interface ChangeLanguageModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  /** Source document (locked "Original" row). */
  source: ChangeLanguageDoc | null;
  /** All translated docs (may be empty). */
  translations: ChangeLanguageDoc[];
  /** Currently active doc id (source or translation). */
  activeDocId: string;
  /**
   * Called when the user picks a row. Parent decides — navigate (main
   * editor) vs. swap right panel (split view).
   */
  onSelectDoc: (docId: string) => void;
  /**
   * #2241: API-sourced translation quote. Rendered next to Start so
   * the user sees the real price before committing. Undefined while
   * loading; the panel hides the badge in that case.
   */
  quote?: TranslationQuote;
  quoteLoading?: boolean;
  quoteError?: boolean;
  onRetryQuote?: () => void;
  /**
   * #2241: upper-case codes currently being translated server-side.
   * Rendered as disabled rows at the bottom of the list. Owned by the
   * parent so the state survives the modal closing (the AC requires
   * the in-progress row to still be there the next time the dialog
   * opens).
   */
  inProgressCodes?: string[];
  /**
   * #2241: fires the translation. Called when the user hits Start in
   * the AddTranslationPanel. The modal closes once the mutation has
   * been handed off — polling / completion lives in `useTranslationQueue`.
   */
  onStartTranslation?: (code: string) => Promise<void>;
  /**
   * The doc the user opened via the URL — the one that stays as the
   * primary when they close split view. Distinct from `activeDocId`,
   * which in right-panel mode points at whichever translation is
   * pinned on the right. We render a small "You're viewing" badge on
   * this row so the user knows which row collapses the split pane.
   */
  primaryDocId?: string;
}

export function ChangeLanguageModal({
  isOpen,
  onClose,
  source,
  translations,
  activeDocId,
  onSelectDoc,
  quote,
  quoteLoading,
  quoteError,
  onRetryQuote,
  inProgressCodes = [],
  onStartTranslation,
  primaryDocId
}: ChangeLanguageModalProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleClose = () => {
    setShowAdd(false);
    onClose();
  };

  const handleCreate = async (code: string) => {
    if (!source || !onStartTranslation) return;
    setCreating(true);
    try {
      const translation = onStartTranslation(code);
      // The endpoint finishes the translation before responding. Hand the
      // request to the parent queue and release the modal while it runs.
      setShowAdd(false);
      onClose();
      await translation;
    } catch {
      /* The parent queue shows the failure and removes the pending language. */
    } finally {
      setCreating(false);
    }
  };

  const existingCodes = [
    ...(source?.language ? [source.language.toUpperCase()] : []),
    ...translations
      .map(td => td.language?.toUpperCase())
      .filter((c): c is string => !!c),
    ...inProgressCodes
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="small"
      title={t('change-language')}
      caption={t('select-language-to-view-translation')}
    >
      {showAdd ? (
        <AddTranslationPanel
          existingCodes={existingCodes}
          quote={quote}
          quoteLoading={quoteLoading}
          quoteError={quoteError}
          onRetryQuote={onRetryQuote}
          onCancel={() => setShowAdd(false)}
          onCreate={handleCreate}
          creating={creating}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {/* Original row — clickable so the user can switch BACK to
              single-pane view. Previously it was a static display and
              there was no way to leave split view once a translation
              had been picked. */}
          <div className="flex flex-col gap-1.5">
            <span
              className="text-xs font-semibold"
              style={{ color: getColour('normalText') }}
            >
              {t('original')}
            </span>
            {source ? (
              <SelectableRow
                label={nameForCode(source.language) || t('unknown')}
                isPrimary={source.id === primaryDocId}
                isActive={source.id === activeDocId}
                isPrimaryAndSplit={
                  source.id === primaryDocId && primaryDocId !== activeDocId
                }
                idleBg={
                  source.id === activeDocId
                    ? getColour('thirdLayer')
                    : getColour('firstLayer')
                }
                onSelect={() => onSelectDoc(source.id)}
                viewingLabel={t('youre-viewing')}
                collapseHint={t('switch-back-to-single-pane-hint')}
                activeColor={getColour('activeText')}
                idleColor={getColour('icon')}
                borderColor={getColour('border')}
                textColor={getColour('normalText')}
              />
            ) : (
              <div
                className="rounded-xl border px-3 py-2 text-sm"
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: getColour('firstLayer'),
                  color: getColour('normalText')
                }}
              >
                —
              </div>
            )}
          </div>

          {/* Translations list. */}
          <div className="flex flex-col gap-1.5">
            <span
              className="text-xs font-semibold"
              style={{ color: getColour('normalText') }}
            >
              {t('translations')}
            </span>
            {translations.length === 0 && inProgressCodes.length === 0 ? (
              <p
                className="text-xs opacity-70 py-2"
                style={{ color: getColour('inactiveText') }}
              >
                {t('no-translations-yet')}
              </p>
            ) : (
              <div
                role="radiogroup"
                aria-label={t('translations')}
                className="flex flex-col gap-1.5"
              >
                {translations.map(td => {
                  const active = td.id === activeDocId;
                  const isPrimary = td.id === primaryDocId;
                  return (
                    <SelectableRow
                      key={td.id}
                      label={
                        nameForCode(td.language) ||
                        td.language?.toUpperCase() ||
                        t('unknown')
                      }
                      isPrimary={isPrimary}
                      isActive={active}
                      isPrimaryAndSplit={isPrimary && !active}
                      idleBg={
                        active
                          ? getColour('thirdLayer')
                          : getColour('secondLayer')
                      }
                      onSelect={() => onSelectDoc(td.id)}
                      viewingLabel={t('youre-viewing')}
                      collapseHint={t('switch-back-to-single-pane-hint')}
                      activeColor={getColour('activeText')}
                      idleColor={getColour('icon')}
                      borderColor={getColour('border')}
                      textColor={getColour('normalText')}
                    />
                  );
                })}
                {/* #2241: in-progress rows — not selectable until the
                    server reports the doc finished. */}
                {inProgressCodes.map(code => (
                  <div
                    key={`pending-${code}`}
                    data-testid={`in-progress-${code}`}
                    aria-busy="true"
                    aria-label={t('translation-in-progress')}
                    className="flex items-center justify-between rounded-xl border px-3 py-2 text-sm opacity-70"
                    style={{
                      borderColor: getColour('border'),
                      backgroundColor: getColour('secondLayer'),
                      color: getColour('inactiveText')
                    }}
                  >
                    <span className="truncate">
                      {nameForCode(code) || code.toUpperCase()}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <Loader2 size={12} className="animate-spin" aria-hidden />
                      {t('in-progress', { defaultValue: 'In progress' })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pt-1">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowAdd(true)}
              label={t('add-a-translation')}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
